import { useAuthFetch, useGuardedEffect } from "@p0security/frontend-core";
import { DatabricksApiRoutes } from "@p0security/integrations-databricks-shared/api";
import {
  CATALOG_KEY_FORMAT,
  parseCatalogKey,
  renderCatalogKey,
} from "@p0security/integrations-databricks-shared/catalog-key";
import { catalogNameError } from "@p0security/integrations-databricks-shared/components";
import { installedItem } from "@p0security/shared/install/installed";
import { Typography } from "@p0security/ui";
import { Fragment, type ReactNode, useCallback, useRef, useState } from "react";

import {
  type InstallContext,
  errorMessage,
  installedKeys,
  itemLabel,
  readListerItems,
} from "./options.ts";

const { Text } = Typography;

/** Beyond this many, the rest are counted rather than named. */
const MAX_SHOWN = 10;

type Listing =
  | { type: "failed"; reason: string }
  | { type: "listed"; names: string[] };

/** An installed workspace, and its name in Databricks, if P0 has read it. */
type Workspace = { id: string; label: string | undefined };

/** The installed workspaces, in the order of their names in lists. */
const installedWorkspaces = (context: InstallContext): Workspace[] =>
  [...installedKeys(context, "workspace")]
    .map((id) => ({
      id,
      label: installedItem("workspace", context.config, id)?.label,
    }))
    .sort((a, b) =>
      itemLabel(a.id, a.label).localeCompare(itemLabel(b.id, b.label))
    );

/**
 * The workspace ID in a key the customer is typing: whatever follows its last
 * `@`, as in a whole key. Unlike `parseCatalogKey`, it doesn't need the
 * catalog's name, so a customer can type the workspace first.
 */
const typedWorkspaceId = (key: string | undefined) =>
  key?.includes("@") ? key.slice(key.lastIndexOf("@") + 1).trim() : undefined;

/**
 * The names, in lowercase, of the catalogs added through `workspace` or
 * another workspace in its account, in any state. Install refuses a catalog
 * that P0 already manages, or is still installing, through a workspace on the
 * same metastore, and a metastore belongs to one account. An account with
 * several metastores leaves out more than it needs to, which the suggestions'
 * copy allows for.
 */
const addedCatalogNames = (context: InstallContext, workspace: string) => {
  const accountOf = (id: string) =>
    installedItem("workspace", context.config, id)?.account;
  const account = accountOf(workspace);
  // A config has no `catalog` until its first catalog item is saved.
  return new Set(
    Object.keys(context.config.catalog ?? {}).flatMap((key) => {
      const parsed = parseCatalogKey(key);
      const isNear =
        !!parsed &&
        (parsed.workspaceId === workspace ||
          (!!account && accountOf(parsed.workspaceId) === account));
      return isNear ? [parsed.catalog.toLowerCase()] : [];
    })
  );
};

/** `items`, separated by commas. */
const Joined = ({ items }: { items: { key: string; node: ReactNode }[] }) => (
  <>
    {items.map(({ key, node }, ix) => (
      <Fragment key={key}>
        {ix > 0 && ", "}
        {node}
      </Fragment>
    ))}
  </>
);

const Keys = ({ keys }: { keys: string[] }) => (
  <>
    <Joined
      items={keys
        .slice(0, MAX_SHOWN)
        .map((key) => ({ key, node: <Text code>{key}</Text> }))}
    />
    {keys.length > MAX_SHOWN && ` and ${keys.length - MAX_SHOWN} more`}
  </>
);

/**
 * Every installed workspace, by name and ID. Not capped as the catalogs are:
 * the customer needs the ID of the one they want, and each workspace is
 * installed with Terraform of its own, so a tenant has few.
 */
const Workspaces = ({ workspaces }: { workspaces: Workspace[] }) => (
  <Joined
    items={workspaces.map(({ id, label }) => ({
      key: id,
      node: label ? (
        <>
          {label} (<Text code>{id}</Text>)
        </>
      ) : (
        <Text code>{id}</Text>
      ),
    }))}
  />
);

/**
 * Describes the catalog key field of the installer's "add a catalog" step,
 * from what the customer has typed in it so far: what the key is, then, as
 * suggestions only, the installed workspaces whose IDs it can end in, or,
 * once it ends in one, the keys of the catalogs that P0 can already see
 * through that workspace and doesn't already manage. Unity Catalog lists only
 * the catalogs a principal has privileges on, and the principal has none on a
 * catalog until it holds MANAGE, so the catalog being added is often missing.
 */
export const CatalogSuggestions = ({
  context,
  typedKey,
}: {
  context: InstallContext;
  typedKey: string | undefined;
}) => {
  const workspaces = installedWorkspaces(context);
  const typedId = typedWorkspaceId(typedKey);
  const workspace = workspaces.find(({ id }) => id === typedId);
  // Only a different workspace lists again, not each change to the name.
  const workspaceId = workspace?.id;

  const [listing, setListing] = useState<Listing>();
  const [isListing, setIsListing] = useState(false);
  // Stable, because the effect re-runs whenever its error handler changes.
  const onListingError = useCallback((error: unknown) => {
    setListing({ type: "failed", reason: errorMessage(error) });
  }, []);
  // `useAuthFetch` reports a failed fetch even after its cancellation fires,
  // so its handler only records why, and the effect, which knows whether its
  // fetch is still current, shows it. It's also called with nothing when a
  // fetch succeeds.
  const fetchError = useRef<string>();
  const onFetchError = useCallback((error: string | undefined) => {
    fetchError.current = error;
  }, []);
  // Its own fetch, rather than the page's `context.authFetch`: that one would
  // disable the form while it lists, and report a failed listing as the
  // install's error.
  const authFetch = useAuthFetch(onFetchError);

  // The effect sets `isListing`, rather than the fetch: a fetch that a newer
  // one superseded would otherwise clear it as it ends, while the newer one is
  // still listing.
  useGuardedEffect(
    (cancellation) => async () => {
      cancellation.guard(setListing)(undefined);
      cancellation.guard(setIsListing)(!!workspaceId);
      if (!workspaceId) return;
      try {
        const response = await authFetch(
          DatabricksApiRoutes.catalogs.path(workspaceId),
          { cancellation }
        );
        if (!response) {
          if (fetchError.current)
            cancellation.guard(onListingError)(fetchError.current);
          return;
        }
        const items = await readListerItems(response);
        cancellation.guard(setListing)({
          type: "listed",
          names: items.map(({ key }) => key),
        });
      } finally {
        cancellation.guard(setIsListing)(false);
      }
    },
    [authFetch, onListingError, workspaceId],
    onListingError
  );

  const format = (
    <>
      <Text code>{CATALOG_KEY_FORMAT}</Text>: the catalog&apos;s name, and the
      ID of the installed workspace that P0 reaches it through.
    </>
  );
  if (!workspace) {
    return workspaces.length ? (
      <>
        {format} Installed workspaces: <Workspaces workspaces={workspaces} />.
      </>
    ) : (
      format
    );
  }

  const through = itemLabel(workspace.id, workspace.label);
  const added = addedCatalogNames(context, workspace.id);
  // A name the key can't carry isn't suggested, since install would refuse it.
  const suggested =
    listing?.type === "listed"
      ? listing.names
          .filter(
            (catalog) =>
              !added.has(catalog.toLowerCase()) && !catalogNameError(catalog)
          )
          .map((catalog) =>
            renderCatalogKey({ catalog, workspaceId: workspace.id })
          )
      : [];

  return (
    <>
      {format}{" "}
      {isListing ? (
        `Listing the catalogs P0 can already see through ${through}...`
      ) : !listing ? null : listing.type === "failed" ? (
        <Text type="warning">{listing.reason}</Text>
      ) : suggested.length ? (
        <>
          Through {through}, P0 can already see <Keys keys={suggested} />. A
          catalog that isn&apos;t listed works too: P0 sees a catalog only once
          its service principal has privileges on it.
        </>
      ) : (
        `P0 can't see any catalogs to add through ${through} yet: it sees a catalog only once its service principal has privileges on it.`
      )}
    </>
  );
};
